import type { ProcessFrameParams } from '@/types';

import { PASSENGER_TRAIN } from '@/utils/constants';
import {
  ARRIVAL_THRESHOLD,
  CIRCLE_BG_COLORS,
  MAX_POOL_SIZE,
  Z_STACK_STEP,
  ZOOM_11,
} from '@/utils/constants/map';
import { computeTrainPosition } from '@/utils/helpers/train-simulation/compute-train-position';
import { isPointWithinBounds } from '@/utils/helpers/train-simulation/is-point-within-bounds';
import { scatterStoppedTrains } from '@/utils/helpers/train-simulation/scatter-stopped-trains';

interface ProcessFrameParamsWithZoom extends ProcessFrameParams {
  zoom: number;
}

export const processTrainFrame = ({
  now,
  visibleTrainIds,
  trainStateMap,
  pool,
  bounds,
  lockedId,
  stoppedGroups,
  zoom,
}: ProcessFrameParamsWithZoom): number => {
  if (!bounds) {
    return 0;
  }

  let activeIndex = 0;
  stoppedGroups.clear();
  const tempPos: [number, number] = [0, 0];
  const isLarge = zoom >= ZOOM_11;

  for (let i = 0; i < visibleTrainIds.length; i++) {
    if (activeIndex >= MAX_POOL_SIZE) {
      break;
    }

    const id = visibleTrainIds[i];
    const train = trainStateMap.get(id);

    if (!train || !train.path || train.path.length === 0) {
      continue;
    }

    const bearing = computeTrainPosition(now, train, tempPos);
    const isLocked = train.id === lockedId;

    if (!isLocked && !isPointWithinBounds(tempPos, bounds)) {
      continue;
    }

    let item = pool[activeIndex];

    if (!item) {
      item = pool[activeIndex] = {
        id: '',
        position: [0, 0, 0],
        bearing: 0,
        color: [0, 0, 0],
        lineNumber: '',
        journeyNumber: '',
        journeyCategory: PASSENGER_TRAIN,
        journeyType: '',
        journeyState: '',
        deviationCategory: 'tooLate',
        deviationTime: '',
        atCurrentStop: false,
        hasActiveDangerousGoods: false,
        journeyFinished: false,
        willStart: false,
        isOperational: false,
      };
    }

    const statusColor = CIRCLE_BG_COLORS[train.deviationCategory] ?? CIRCLE_BG_COLORS.default;

    item.id = train.id;
    item.position[0] = tempPos[0];
    item.position[1] = tempPos[1];
    item.position[2] = isLarge ? i * Z_STACK_STEP : 0;
    item.bearing = bearing;
    item.color = train.journeyFinished ? CIRCLE_BG_COLORS.stopped : statusColor;
    item.lineNumber = train.lineNumber;
    item.journeyNumber = train.journeyNumber;
    item.journeyCategory = train.journeyCategory;
    item.journeyType = train.journeyType;
    item.journeyState = train.journeyState;
    item.deviationCategory = train.deviationCategory;
    item.deviationTime = train.deviationTime;
    item.atCurrentStop = train.atCurrentStop;
    item.hasActiveDangerousGoods = train.hasActiveDangerousGoods;
    item.journeyFinished = train.journeyFinished;
    item.willStart = train.willStart;
    item.isOperational = train.isOperational;

    const elapsed = now - train.startTime;
    const progress = train.duration > 0 ? elapsed / train.duration : 0;

    const isStoppedAtStation =
      train.atCurrentStop ||
      train.willStart ||
      train.journeyFinished ||
      progress > ARRIVAL_THRESHOLD ||
      progress < 0;

    if (isStoppedAtStation && !train.isOperational) {
      const lonKey = Math.round(tempPos[0] * 1000);
      const latKey = Math.round(tempPos[1] * 1000);
      const key = lonKey * 1_000_000 + latKey;
      const group = stoppedGroups.get(key);

      if (group) {
        group.push(activeIndex);
      } else {
        stoppedGroups.set(key, [activeIndex]);
      }
    }

    activeIndex++;
  }

  scatterStoppedTrains(stoppedGroups, pool);

  return activeIndex;
};
